import { useState } from "react";
import { RotateCw } from "lucide-react";
import { rerunBook, type Book } from "../../api/pqx";
import { useMe } from "../../components/MeProvider";

/** States the engine has finished with, so it can run the book again. */
const RERUNNABLE = new Set<Book["state"]>(["imported", "failed", "import_failed", "gate_failed"]);

/** Who may rerun a book: its uploader or an admin, once the engine has finished. */
export function canRerunBook(
  book: Pick<Book, "reviewer" | "state">,
  me: { id: string; can: { admin: boolean } } | null | undefined,
): boolean {
  if (!RERUNNABLE.has(book.state)) return false;
  return me?.id === book.reviewer || Boolean(me?.can.admin);
}

/** "27" or "27-30", the first page no later than the last. */
export function pagesProblem(raw: string): string | null {
  const pages = raw.replace(/\s+/g, "");
  if (!pages) return null;
  if (!/^\d+(-\d+)?$/.test(pages)) return 'Write one page, like "27", or a range, like "27-30".';
  const [first, last = first] = pages.split("-").map(Number) as [number, number?];
  if (first < 1) return "Pages start at 1.";
  if (last! < first) return "The first page must come before the last.";
  return null;
}

/**
 * "Rerun": sends a finished book back through the engine.
 *
 * It isn't a fresh start. The engine keeps every page and call that worked and
 * redoes only what failed; a page range reads those pages again from scratch,
 * for a page whose questions came back wrong. The book's questions are then
 * updated in place. A reviewer's own changes are kept, and a live question
 * waits for a reviewer before anything on it changes.
 */
export function RerunBookButton({ book, onRerun }: { book: Book; onRerun: (book: Book) => void }) {
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!canRerunBook(book, me)) return null;

  const problem = pagesProblem(pages);
  const isVerify = book.kind === "verify";

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const cleaned = pages.replace(/\s+/g, "");
      onRerun(await rerunBook(book.book_id, cleaned || undefined));
      setOpen(false);
      setPages("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't rerun it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-semibold hover:bg-surface-high"
      >
        <RotateCw size={14} aria-hidden="true" /> Rerun
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={`Rerun ${book.filename}`}
      className="w-full rounded-xl border border-border bg-surface p-4 text-[13px] sm:w-[26rem]"
    >
      <p className="font-semibold">Rerun “{book.filename}”</p>
      <p className="mt-1 text-text-muted">
        {isVerify
          ? "The engine checks again only the questions it couldn't check last time."
          : "The engine keeps every page it read and redoes only what failed."}{" "}
        Your edits, approvals and dismissed issues are kept. Live questions don't change until you
        accept the new values.
      </p>
      {isVerify ? null : (
        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Pages to read again (optional)
          </span>
          <input
            value={pages}
            onChange={(e) => setPages(e.target.value)}
            placeholder="e.g. 27 or 27-30"
            inputMode="numeric"
            aria-describedby="rerun-pages-help"
            className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-[13px] outline-none focus:border-accent"
          />
          <span id="rerun-pages-help" className="mt-1 block text-[12px] text-text-muted">
            For pages whose questions came back wrong. Leave empty to redo only what failed.
          </span>
        </label>
      )}
      {problem ? (
        <p role="alert" className="mt-2 text-[12px] text-warning">
          {problem}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-[12px] text-negative">
          {error}
        </p>
      ) : null}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={busy || problem !== null}
          onClick={() => void send()}
          className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
        >
          {busy ? "Sending…" : "Rerun"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="rounded-lg border border-border px-4 py-2 text-[13px]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/** What the last rerun did, in a line, once its result is in. */
export function rerunSummary(rerun: NonNullable<Book["rerun"]>): string {
  const what = rerun.pages ? `pages ${rerun.pages} read again` : "what failed redone";
  const when = new Date(rerun.at).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  if (!rerun.outcome) return `Rerunning: ${what}. Asked by ${rerun.by}, ${when}.`;
  const { updated, added, held, dropped } = rerun.outcome;
  const parts = [
    updated ? `${updated} updated` : "",
    added ? `${added} added` : "",
    held ? `${held} live ${held === 1 ? "question waits" : "questions wait"} for you` : "",
    dropped ? `${dropped} no longer found` : "",
  ].filter(Boolean);
  return `Last rerun (${what}, by ${rerun.by}, ${when}): ${parts.length ? parts.join(", ") : "nothing changed"}.`;
}
